import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { ProductFilter } from './ProductFilter';
import { Product, ProductVisibility } from 'core/product/Product';
import { ColumnDefinition, renderColumn, sortableColumn } from 'components/TableColumn/TableColumn';
import formatters from './listFormatters';
import _, { camelCase } from 'lodash';
import styles from './productList.module.scss';
import { ProductListDataContext, ProductListDataContextType } from './ProductListDataContext';
import i18n from 'i18n';

export enum ProductListColumns {
  ID = 'productId',
  NAME = 'name',
  IMAGE = 'imgLink',
  CATEGORY = 'category',
  LISTPRICE = 'listPrice',
  STOCKTYPE = 'stockType',
  AVAILABILITY = 'availability'
}

export enum ProductListType {
  BASIC = 'basic'
}

export type ProductListState = {
  readonly selectedProductListType: ProductListType;
};

export const useProductListModel = (
  filterSet: ProductFilter,
  productList: Product[],
  allProducts: Product[],
  singleSelect?: boolean
) => {

  const productListDataContext: ProductListDataContextType = useContext(ProductListDataContext);
  const { selectedProducts: initSelectedProducts, handleSelectedProducts, handleFilterChanged } = productListDataContext;
  const availbleProducts = _.uniqBy(allProducts.concat(initSelectedProducts), 'productId');
  const [selectedProducts, setSelectedProducts] = useState<string[]>(initSelectedProducts.map(product => product.productId));
  const [state, setState] = useState({
    selectedProductListType: ProductListType.BASIC
  } as ProductListState);

  useEffect(() => {
    handleFilterChanged(filterSet);
  }, [filterSet, handleFilterChanged]);

  const columnDefinition = (columnName, sortable: boolean = false, customLabel?: string): ColumnDefinition => ({
    ...sortableColumn(columnName, customLabel ? customLabel : `productList.labels.${columnName}`, sortable),
    classes: () => styles[columnName],
    headerClasses : () => styles[columnName]
  });

  const onSelect = (productId: string) => {
    const product = availbleProducts.find(product => product.productId === productId);
    if (!product) {
      return;
    }
    const isSelected = selectedProducts.indexOf(productId) > -1;
    // Hidden products can only be deselected (removed), not selected
    if (product.visibility === ProductVisibility.HIDDEN && !isSelected) {
      return;
    }
    if (isSelected) {
      setSelectedProducts(prevSelectedProducts => prevSelectedProducts.filter(id => id !== productId));
      handleSelectedProducts(prevSelectedProducts => prevSelectedProducts.filter(product => product.productId !== productId));
    } else if (singleSelect) {
      // single-select: replace the whole selection with the latest picked product.
      setSelectedProducts([productId]);
      handleSelectedProducts(() => [product]);
    } else {
      setSelectedProducts(prevSelectedProducts => [...prevSelectedProducts, productId]);
      handleSelectedProducts(prevSelectedProducts => [...prevSelectedProducts, product]);
    }
  };

  const selectableProductList = useMemo(() =>
    productList.filter(product => product.visibility !== ProductVisibility.HIDDEN),
    [productList]
  );

  const selectProductsAfterFilter = useMemo(() =>
    selectedProducts.filter(id => productList.find(product => product.productId === id)),
    [selectedProducts, productList]
  );

  const onSelectAll = () => {
    // In single-select mode "select all this page"
    // makes no sense — silently no-op the additive path. Deselect-all still works.
    if (selectProductsAfterFilter.length === selectableProductList.length) {
      const newSelectedProducts = selectedProducts.filter(id => !selectableProductList.find(product => product.productId === id));
      setSelectedProducts(newSelectedProducts);
      handleSelectedProducts(selectedProducts => selectedProducts.filter(selectedProduct => !selectableProductList.find(product => product.productId === selectedProduct.productId)));
    } else {
      if (singleSelect) {
        return;
      }
      const newSelectedProducts = _.uniq([...selectedProducts, ...selectableProductList.map(product => product.productId)]);
      setSelectedProducts(newSelectedProducts);
      handleSelectedProducts(selectedProducts => _.uniqBy([...selectedProducts, ...selectableProductList], 'productId'));
    }
  };

  const handleRemoveSelect = useCallback(() => {
    setSelectedProducts([]);
    handleSelectedProducts(() => []);
  }, [setSelectedProducts, handleSelectedProducts]);

  const onProductListTypeChange = (selectedProductListType) => {
    setState(prevState => ({ ...prevState, selectedProductListType }));
  };

  const idColumn = renderColumn({
    ...columnDefinition(ProductListColumns.ID),
    formatExtraData: {
      // currentUrl: match.url,
      selectedProducts,
      onSelect
    }
  },
  formatters.nameFormatter,
  _.partial(
    formatters.nameHeaderFormatter,
    () => ({
      productsOfCurrentConditionLength: selectableProductList.length,
      selectedProductsOfCurrentCondition: selectProductsAfterFilter,
      hasSelectedProductsOfOtherCondition: selectedProducts.length > selectProductsAfterFilter.length,
      onSelectAll,
      singleSelect
    })
  ));

  const basicColumns = _.compact([
    idColumn,
    renderColumn(columnDefinition(ProductListColumns.IMAGE), formatters.imageFormatter),
    renderColumn(columnDefinition(ProductListColumns.CATEGORY)),
    renderColumn(columnDefinition(ProductListColumns.LISTPRICE)),
    renderColumn(columnDefinition(ProductListColumns.STOCKTYPE), value => value ? i18n.t<string>(`productHome.filters.options.${value.toLowerCase()}`) : 'N/A'),
    renderColumn(columnDefinition(ProductListColumns.AVAILABILITY), value => i18n.t<string>(`productList.labels.${camelCase(value)}`))
  ]);

  const typeColumnsMap = {
    [ProductListType.BASIC]: basicColumns
  };

  return {
    state,
    selectedProducts,
    availbleProducts,
    columns: typeColumnsMap[state.selectedProductListType],
    onProductListTypeChange,
    handleRemoveSelect,
    handleRemoveProduct: onSelect
  };
};
